import { CustomerInfo, Purchases, PurchasesOffering, PurchasesPlugin } from '@revenuecat/purchases-capacitor';
import type {
  IPurchaseStrategy,
  CrossPlatformCustomerInfo,
  CrossPlatformOffering,
  CrossPlatformPackage,
  PurchaseResult,
  OfferingsResult,
} from './types';

export class NativePurchaseStrategy implements IPurchaseStrategy {
  private initialized = false;

  async configure(apiKey: string, userId?: string, email?: string): Promise<void> {
    try {
      await Purchases.configure({
        apiKey,
        appUserID: userId, // Mobile SDK uses appUserID
      });
      if (email) {
        await Purchases.setEmail({ email });
      }
      this.initialized = true;
    } catch (error) {
      console.error('Error initializing RevenueCat on native:', error);
      throw error;
    }
  }

  async getCustomerInfo(): Promise<CrossPlatformCustomerInfo> {
    this.ensureInitialized();

    try {
      const result = await Purchases.getCustomerInfo();

      // Handle different response structures
      const info = 'customerInfo' in result ? result.customerInfo : result;
      return this.normalizeCustomerInfo(info);
    } catch (error) {
      console.error('Error fetching customer info on native:', error);
      throw error;
    }
  }

  async getOfferings(): Promise<OfferingsResult> {
    this.ensureInitialized();

    try {
      const offerings = await Purchases.getOfferings();

      const currentOffering = offerings.current;
      const fullOffer = offerings.all['full_offer'];
      const launchOffer = offerings.all['launch_offer'];
      const offeringToReturn = currentOffering ?? fullOffer ?? launchOffer;
      return {
        current: offeringToReturn ? this.normalizeOffering(offeringToReturn) : undefined,
      };
    } catch (error) {
      console.error('Error fetching offerings on native:', error);
      throw error;
    }
  }

  async purchasePackage(packageToPurchase: CrossPlatformPackage): Promise<PurchaseResult> {
    this.ensureInitialized();

    try {
      const result = await Purchases.purchasePackage({ aPackage: packageToPurchase.originalPackage });

      const updatedInfo = result.customerInfo || result;
      return {
        customerInfo: this.normalizeCustomerInfo(updatedInfo),
      };
    } catch (error) {
      console.error('Error making purchase on native:', error);
      throw error;
    }
  }

  async restorePurchases(): Promise<CrossPlatformCustomerInfo> {
    this.ensureInitialized();

    try {
      const result = await Purchases.restorePurchases();

      const restoredInfo = result.customerInfo || result;
      return this.normalizeCustomerInfo(restoredInfo);
    } catch (error) {
      console.error('Error restoring purchases on native:', error);
      throw error;
    }
  }

  async setCustomerAttributes(attributes: Record<string, string>): Promise<void> {
    this.ensureInitialized();

    try {
      await Purchases.setAttributes(attributes);
      await Purchases.syncAttributesAndOfferingsIfNeeded();
    } catch (error) {
      console.error('Error setting customer attributes on native:', error);
      throw error;
    }
  }

  private ensureInitialized(): void {
    if (!this.initialized) {
      throw new Error('NativePurchaseStrategy not initialized. Call configure() first.');
    }
  }

  private normalizeCustomerInfo(info: CustomerInfo): CrossPlatformCustomerInfo {
    const pro = info.entitlements?.active?.Pro;
    return {
      managementURL: info.managementURL,
      entitlement: pro
        ? {
            identifier: pro?.identifier || '',
            willRenew: pro?.willRenew || false,
            originalPurchaseDate: pro?.originalPurchaseDate || '',
            expirationDate: pro?.expirationDate || null,
          }
        : undefined,
    };
  }

  private normalizeOffering(offering: PurchasesOffering): CrossPlatformOffering {
    return {
      annual: {
        displayTitle: 'Annual',
        hasTrial: true,
        cardBadgeText: `Save ${this.calculateSavePercentage(
          offering.annual?.product.price,
          offering.monthly?.product.price ? offering.monthly?.product.price * 12 : undefined
        )}%`,
        drawerBadgeText: '7 Days Free',
        billingPeriod: 'annually',
        billingPeriodNoun: 'year',
        comparisonPriceString: offering.annual?.product.pricePerWeekString || '',
        comparisonPriceBillingNoun: 'week',
        formattedPrice: offering.annual?.product.priceString || '',
        pricePerWeekString: offering.annual?.product.pricePerWeekString || '',
        pricePerMonthString: offering.annual?.product.pricePerMonthString || '',
        pricePerYearString: offering.annual?.product.pricePerYearString || '',
        originalPackage: offering.annual,
      },
      monthly: {
        displayTitle: 'Monthly',
        hasTrial: true,
        drawerBadgeText: `7 Days Free`,
        billingPeriod: 'monthly',
        billingPeriodNoun: 'month',
        comparisonPriceString: offering.monthly?.product.pricePerYearString || '',
        comparisonPriceBillingNoun: 'year',
        formattedPrice: offering.monthly?.product.priceString || '',
        pricePerWeekString: offering.monthly?.product.pricePerWeekString || '',
        pricePerMonthString: offering.monthly?.product.pricePerMonthString || '',
        pricePerYearString: offering.monthly?.product.pricePerYearString || '',
        originalPackage: offering.monthly,
      },
      weekly: {
        displayTitle: 'Weekly',
        hasTrial: false,
        billingPeriod: 'weekly',
        billingPeriodNoun: 'week',
        comparisonPriceString: offering.weekly?.product.pricePerYearString || '',
        comparisonPriceBillingNoun: 'year',
        formattedPrice: offering.weekly?.product.priceString || '',
        pricePerWeekString: offering.weekly?.product.pricePerWeekString || '',
        pricePerMonthString: offering.weekly?.product.pricePerMonthString || '',
        pricePerYearString: offering.weekly?.product.pricePerYearString || '',
        originalPackage: offering.weekly,
      },
    };
  }

  private calculateSavePercentage(discountedPrice: number | undefined, originalPrice: number | undefined): number | undefined {
    if (!discountedPrice || !originalPrice) {
      return undefined;
    }
    return Math.round(((originalPrice - discountedPrice) / originalPrice) * 100);
  }
}
